import { InfoCircleOutlined } from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { COLOR_TOOLTIP, TEXT_BUTTON_HEADER_LIST } from '../../../config/config';
import { PermissionAuthority } from '../../../permissions/PermissionAuthority';
import { PermissionAuthorityGroups } from '../../../permissions/PermissionAuthorityGroups';
import { PermissionRoles } from '../../../permissions/PermissionRoles';
import perfisStore from '../../../stores/perfisStore';
import rolesStore from '../../../stores/rolesStore';
import { transformToValueLabel } from '../../../utils/functions';
import { YesOrNot } from '../../../utils/selects';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import InputComponet from '../../atoms/InputComponet';
import PermissionComponent from '../../atoms/PermissionComponent';
import SelectComponent from '../../atoms/SelectComponent';
import TextAreaComponent from '../../atoms/TextAreaComponent';
import ContentBox from '../../molecules/ContentBox';
import LoadingWrapper from '../../molecules/LoadingWrapper';
import Permission from '../../molecules/Permission';
import { useAuth } from '../../protected/ProviderAuth';
import { validationSchema } from './schema';

const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <Link to="/cargos">Listagem de Cargos</Link>,
    },
    {
        title: <p>Cadastro de Cargo</p>,
    },
]

const CreateRoles: React.FC = observer(() => {
    const { uuid } = useParams<{ uuid: string }>();
    const { role, loading, listRoles } = rolesStore;
    const [listPerfil, setListPerfil] = useState<any>([]);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [groupSelected, setGroupSelected] = useState<any>();

    useEffect(() => {
        const defaultparams = {
            size: 999,
            page: 0,
            sort: 'created,desc',
            search: '',
        }

        const getInformation = async () => {
            if (uuid) {
                const response = await rolesStore.getRoleByUuid(uuid);
                const listGroup = transformToValueLabel(response.data.authorityGroups, 'uuid', 'name');
                setGroupSelected(listGroup);
            }
        };

        const fetchPerfilList = async () => {
            const response = await perfisStore.getList(defaultparams);
            const filterActive = response.data.content.filter((item: any) => item.active === true)
            const list = transformToValueLabel(filterActive, 'uuid', 'name');
            setListPerfil(list)
        };

        if (hasPermission(PermissionAuthority.GET)) {
            fetchPerfilList();
        }

        getInformation();

        return () => {
            rolesStore.reset();
        };
    }, [uuid]);

    const initialValues: any = {
        name: role?.name || '',
        description: role?.description || '',
        uuid: role?.uuid || undefined,
        hierarchy: { value: role?.hierarchy, label: '' },
        authorityGroups: undefined,
        canManageNonDescendantChildAdministrators: { value: role?.canManageNonDescendantChildAdministrators === true ? 'true' : 'false', label: '' },
        canManageSameHierarchyAdministrators: { value: role?.canManageSameHierarchyAdministrators === true ? 'true' : 'false', label: '' },
    };

    // Faz o filtro da tabela
    const handleSubmit = async (values: any) => {
        let response;

        const mapAuthorities = groupSelected.map((item: any) => {
            return {
                uuid: item.value,
                authorities: []
            }
        })

        if (groupSelected.length === 0) {
            message.error(`Grupo de Usúarios é obrigatório`);
            return;
        }

        const createProps = {
            ...values,
            canManageNonDescendantChildAdministrators: values?.canManageNonDescendantChildAdministrators?.value === 'true' ? true : false,
            canManageSameHierarchyAdministrators: values?.canManageSameHierarchyAdministrators?.value === 'true' ? true : false,
            authorityGroups: mapAuthorities,
            hierarchy: values?.hierarchy?.value,
        }

        if (uuid) {
            response = await rolesStore.editRole(createProps, uuid);
        } else {
            response = await rolesStore.createRoles(createProps);
        }

        if (!response.error) listRouter();
    };


    const listRouter = () => {
        navigate(`/cargos`);
    }

    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} permission={PermissionRoles.GET} textButton={TEXT_BUTTON_HEADER_LIST} onClick={() => listRouter()} />

            <LoadingWrapper loading={loading} title="Cadastro de Cargo" description="Tela para cadastro de Cargo">
                <Permission
                    permissionKeys={[PermissionRoles.POST, PermissionRoles.PUT, PermissionRoles.GET_UUID]}
                    fallback={<PermissionComponent />}
                >
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={validationSchema}
                    >
                        <Form>
                            <ContentBox className="mb-4 p-6" title="Cadastro de Cargo" description='Tela para cadastro de Cargo'>
                                <div className="flex w-full gap-4 pb-4">
                                    <InputComponet label="Nome" name="name" id="name" />
                                </div>

                                <div className="flex w-full gap-4 pb-4">
                                    <TextAreaComponent label="Descrição" name="description" id="description" />
                                </div>

                                <div className="flex w-full gap-4 pb-4">
                                    <SelectComponent
                                        label="Hierarquia"
                                        name="hierarchy"
                                        options={[
                                            { label: "Usuário Padrão", value: 1 },
                                            { label: "Usuário Avançado", value: 2 },
                                            { label: "Administrador", value: 3 },
                                        ]}
                                    />

                                    <Permission permissionKeys={[PermissionAuthorityGroups.GET]} inputType name="Grupo de Usúarios">
                                        <SelectComponent defaultValue={listRoles} onChange={(value, option) => setGroupSelected(option)} mode="multiple" label="Grupo de Usúarios" name="authorityGroups" options={listPerfil} />
                                    </Permission>
                                </div>

                                <div className="flex w-full gap-4 pb-4">
                                    <SelectComponent suffixIcon={
                                        <div className="pr-5 pt-0.5">
                                            <Tooltip color={COLOR_TOOLTIP} title="Administradores com este cargo não têm permissão para criar, editar ou visualizar outros administradores da mesma hierarquia.">
                                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                                            </Tooltip>
                                        </div>
                                    } label="Gerenciar mesma hierarquia?" name="canManageNonDescendantChildAdministrators" options={YesOrNot} />
                                    <SelectComponent suffixIcon={
                                        <div className="pr-5 pt-0.5">
                                            <Tooltip color={COLOR_TOOLTIP} title="Administradores com este cargo possuem restrições: eles não podem criar, editar ou visualizar administradores associados a outros administradores.">
                                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                                            </Tooltip>
                                        </div>
                                    } label="Gerenciar não vinculados?" name="canManageSameHierarchyAdministrators" options={YesOrNot} />
                                </div>
                            </ContentBox>

                            <div className="flex justify-end">
                                <Permission permissionKeys={[PermissionRoles.PUT, PermissionRoles.POST]} buttonType>
                                    <ActionButton htmlType="submit" actionType="primary">{uuid ? 'Atualizar' : 'Cadastrar'}</ActionButton>
                                </Permission>
                            </div>
                        </Form>
                    </Formik >
                </Permission>
            </LoadingWrapper>
        </>
    );
});

export default CreateRoles;